"use client";

import type { ButtonHTMLAttributes } from "react";
import { cx } from "../cx.ts";
import { Icon, type IconName } from "./icon.tsx";

type Variant = "ghost" | "secondary" | "danger";
type Size = "sm" | "md";

const VARIANTS: Record<Variant, string> = {
  ghost: "text-ink-muted hover:bg-surface-subtle hover:text-ink-body",
  secondary: "border border-line bg-surface text-ink-body hover:bg-surface-subtle",
  danger: "text-danger hover:bg-danger-soft",
};

const SIZES: Record<Size, string> = {
  sm: "size-8",
  md: "size-9",
};

export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  icon: IconName;
  /**
   * The control's accessible name, and its only one — there is no visible
   * text. Required rather than optional so the compiler catches an unnamed
   * button, and a *prop* rather than a bare `aria-label` attribute so that a
   * hardcoded English string at an app call site is also caught by
   * `untranslated-strings.test.ts`. App callers pass `t(...)`.
   */
  label: string;
  variant?: Variant;
  size?: Size;
  iconSize?: number;
};

export function IconButton({
  icon,
  label,
  variant = "ghost",
  size = "md",
  iconSize,
  className,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cx(
        "inline-flex shrink-0 items-center justify-center rounded-control transition-colors",
        "duration-150 focus-visible:outline-2 focus-visible:outline-offset-2",
        "focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-50",
        // A square is safe here and nowhere else: the control carries no
        // translated label, so there is no Khmer string to overflow it.
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      <Icon name={icon} size={iconSize ?? (size === "sm" ? 16 : 18)} />
    </button>
  );
}
